import { c as _c } from "react/compiler-runtime";
import { useChains } from "@opensea/app-kit";
import { classNames, Flex, FlexColumn } from "@opensea/ui-kit";
import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { expandedSearchTransitionClassNames } from "./constants";
import { ExpandedSearchMenuInput } from "./ExpandedSearchMenuInput";
import { ExpandedSearchMenuResults } from "./ExpandedSearchMenuResults";
import { ExpandedSearchSidebar } from "./ExpandedSearchSidebar";
import { useExpandedSearchStore } from "./ExpandedSearchStore";
import { useExpandedSearchTopCurrencies, useExpandedSearchTrendingCollections } from "./hooks";
export const EXPANDED_SEARCH_SELECTOR = "expanded-search-menu";

/**
 * Helper function to get available chains for a category
 * Based on the chainType used in ExpandedSearchMenuResults
 */
function getAvailableChainsForCategory(category, allChains, evmChains) {
  function getChainIdentifiers() {
    switch (category) {
      case "all":
      case "tokens":
        return allChains.map(chain => chain.identifier);
      case "collections":
      case "items":
        return evmChains.map(chain => chain.identifier);
      case "wallets":
        return [];
      // No chain filter for wallets
      default:
        return [];
    }
  }
  return ["all", ...getChainIdentifiers()];
}
export function ExpandedSearchMenu() {
  const $ = _c(31);
  const {
    open,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery
  } = useExpandedSearchStore(useShallow(_temp));
  const {
    chains,
    evmChains
  } = useChains();
  const [selectionMethod, setSelectionMethod] = useState("keyboard");
  const [selectedChain, setSelectedChain] = useState("all");
  let t0;
  let t1;
  if ($[0] !== open || $[1] !== setSearchQuery || $[2] !== setSelectedCategory) {
    t0 = () => {
      if (!open) {
        setSelectedCategory("all");
        setSearchQuery("");
        setSelectedChain("all");
      }
    };
    t1 = [open, setSelectedCategory, setSearchQuery];
    $[0] = open;
    $[1] = setSearchQuery;
    $[2] = setSelectedCategory;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== chains || $[6] !== evmChains || $[7] !== selectedCategory || $[8] !== selectedChain) {
    t2 = () => {
      const availableChains = getAvailableChainsForCategory(selectedCategory, chains, evmChains);
      if (!availableChains.includes(selectedChain)) {
        setSelectedChain("all");
      }
    };
    t3 = [selectedCategory, chains, evmChains, selectedChain];
    $[5] = chains;
    $[6] = evmChains;
    $[7] = selectedCategory;
    $[8] = selectedChain;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  const t4 = !open;
  let t5;
  if ($[11] !== selectedCategory || $[12] !== selectedChain || $[13] !== t4) {
    t5 = {
      pause: t4,
      selectedCategory,
      selectedChain,
      isVerified: true
    };
    $[11] = selectedCategory;
    $[12] = selectedChain;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const trendingCollections = useExpandedSearchTrendingCollections(t5);
  const t6 = !open;
  let t7;
  if ($[15] !== selectedCategory || $[16] !== selectedChain || $[17] !== t6) {
    t7 = {
      pause: t6,
      selectedCategory,
      selectedChain
    };
    $[15] = selectedCategory;
    $[16] = selectedChain;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const topCurrencies = useExpandedSearchTopCurrencies(t7);
  const resultsRef = useRef(null);
  let t8;
  if ($[19] !== setSearchQuery) {
    t8 = function updateQuery(newQuery) {
      setSearchQuery(newQuery);
      resultsRef.current?.setSelectedItemIndex(0);
    };
    $[19] = setSearchQuery;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const updateQuery = t8;
  let t9;
  if ($[21] !== open || $[22] !== searchQuery || $[23] !== selectedCategory || $[24] !== selectedChain || $[25] !== selectionMethod || $[26] !== setSelectedCategory || $[27] !== topCurrencies || $[28] !== trendingCollections || $[29] !== updateQuery) {
    t9 = open ? <FlexColumn className={classNames("top-0 size-full overflow-hidden border-0 lg:h-4/6 lg:w-1/2 lg:min-w-[900px] lg:rounded-xl lg:border lg:border-border-1", expandedSearchTransitionClassNames, open ? "lg:shadow-expanded-search-menu" : "translate-y-5 lg:translate-y-0 lg:scale-95")} id={EXPANDED_SEARCH_SELECTOR}><ExpandedSearchMenuInput onKeyDown={e => {
        setSelectionMethod("keyboard");
        bb25: switch (e.key) {
          case "ArrowUp":
            {
              resultsRef.current?.previous();
              break bb25;
            }
          case "ArrowDown":
            {
              resultsRef.current?.forward();
              break bb25;
            }
          case "Enter":
            {
              resultsRef.current?.navigate({
                openInNewTab: e.metaKey
              });
            }
        }
      }} query={searchQuery} selectedCategory={selectedCategory} setQuery={updateQuery} /><Flex className="min-w-0 flex-1 flex-col overflow-hidden lg:flex-row"><ExpandedSearchSidebar onCategoryChangeAction={setSelectedCategory} selectedCategory={selectedCategory} /><ExpandedSearchMenuResults expandedSearchMenuResultsRef={resultsRef} onPointerMove={() => {
          setSelectionMethod("mouse");
        }} onSelectChainAction={setSelectedChain} query={searchQuery} selectedCategory={selectedCategory} selectedChain={selectedChain} selectionMethod={selectionMethod} setQuery={updateQuery} topCurrencies={topCurrencies} trendingCollections={trendingCollections} /></Flex></FlexColumn> : null;
    $[21] = open;
    $[22] = searchQuery;
    $[23] = selectedCategory;
    $[24] = selectedChain;
    $[25] = selectionMethod;
    $[26] = setSelectedCategory;
    $[27] = topCurrencies;
    $[28] = trendingCollections;
    $[29] = updateQuery;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function _temp(state) {
  return {
    open: state.open,
    selectedCategory: state.selectedCategory,
    setSelectedCategory: state.setSelectedCategory,
    searchQuery: state.searchQuery,
    setSearchQuery: state.setSearchQuery
  };
}